import { useTranslation } from 'react-i18next';

import { CustomModal, CustomModalProps } from 'components/shared';
import { useErrorDescriptions } from 'constants/errorDescriptions';
import { useModal } from 'hooks/useModal';
import { OrderDetailsDTO } from 'interfaces';
import { HTTPService } from 'service';
import { logNetworkError } from 'utils/logNetworkError';

import styles from './AcceptOrderModal.module.css';

type AcceptOrderModalProps = CustomModalProps & {
   selectedOrderId: string;
   isOrderNonstandard?: boolean;
   onSuccessAcceptOrder: (updatedOrder: OrderDetailsDTO) => Promise<void>;
};

const AcceptOrderModal = ({
   selectedOrderId,
   isOrderNonstandard,
   onSuccessAcceptOrder,
   onClose,
   ...props
}: AcceptOrderModalProps) => {
   const { isLoading, errorInfo, setIsLoading, setErrorInfo, closeModalHandler } = useModal({
      onClose,
   });

   const { DEFAULT_ERROR_DESCRIPTIONS } = useErrorDescriptions();
   const { t } = useTranslation();

   const handleAcceptOrder = () => {
      setIsLoading(true);
      HTTPService.changeOrderStatus(selectedOrderId, 'ACCEPT')
         .then(response => {
            closeModalHandler();
            return onSuccessAcceptOrder(response.data);
         })
         .catch(error => {
            setErrorInfo(DEFAULT_ERROR_DESCRIPTIONS.UNKNOWN);
            logNetworkError(error);
         })
         .finally(() => setIsLoading(false));
   };

   const title = isOrderNonstandard
      ? t('order_details_page.accept_non_standard_order')
      : t('order_details_page.accept_order');

   const confirmationMessage = isOrderNonstandard
      ? t('accept_order_modal.confirm_nonstandard')
      : t('accept_order_modal.confirm');

   return (
      <CustomModal
         {...props}
         icon="FILE_CHECK"
         innerTitle={title}
         error={errorInfo}
         modalContentClassName={styles.modalContent}
         isLoading={isLoading}
         onClose={closeModalHandler}
         primaryButtonProps={{
            text: title,
            variant: 'filled-primary',
            onClick: handleAcceptOrder,
         }}
         secondaryButtonProps={{
            text: t('common.cancel'),
            variant: 'text',
            onClick: closeModalHandler,
         }}
      >
         <div className={styles.descriptionContainer}>
            <p className={styles.plainText}>{confirmationMessage}</p>
            <p className={styles.boldBlackText}>{t('cancel_pallet_report_modal.irreversible')}</p>
         </div>
      </CustomModal>
   );
};

export default AcceptOrderModal;
